import { MappingEngine } from './MappingEngine';
import { FileMapping } from './types';
import type {
  CsvFileHeaderData,
  FileMetadata,
  FileValidationResult,
  SelectedFile,
  StudyForm,
} from '../components/types';
import type { CsvFile } from '@/types/csvFiles';

// Legacy types from existing codebase
interface LegacySelectedFile {
  source: 'uploaded' | 'csv-importer';
  file?: File;
  csvFile?: { id: number; filename: string };
  metadata: {
    columnCount: number;
    columns: string[];
    sampleColumns: string[];
  };
}

interface LegacyStudyForm {
  id: number;
  name: string;
  type: number; // 1 = main, 2 = subform
}

interface LegacyFileToFormMapping {
  fileId: string;
  formId: number;
  confidence: number;
  matchType: 'exact' | 'fuzzy' | 'manual';
}

/**
 * Adapter functions to bridge new MappingEngine with legacy FormDetectionStep
 */
export class MappingEngineAdapters {
  /**
   * Converts SelectedFile (new format) to format expected by MappingEngine
   */
  static selectedFileToMappingEngineFile(
    file: SelectedFile | (LegacySelectedFile & { headerData?: unknown })
  ): SelectedFile {
    // If it's already the new shape, return as-is.
    if ('validation' in file && 'metadata' in file) {
      return file as SelectedFile;
    }

    const legacy = file as LegacySelectedFile & { headerData?: unknown };

    const validation: FileValidationResult = {
      isValid: true,
      errors: [],
      warnings: [],
    };

    const columns = legacy.metadata?.columns || [];
    const sampleColumns = legacy.metadata?.sampleColumns || [];
    const metadata: FileMetadata = {
      columnCount:
        typeof legacy.metadata?.columnCount === 'number'
          ? legacy.metadata.columnCount
          : columns.length,
      rowCount: 0,
      columns,
      sampleColumns,
      size: legacy.file?.size ?? 0,
    };

    return {
      source: legacy.source,
      file: legacy.file,
      // Legacy CSV file objects are partial; MappingEngine only needs id/filename.
      csvFile: legacy.csvFile as unknown as CsvFile,
      headerData: legacy.headerData as unknown as CsvFileHeaderData | undefined,
      validation,
      metadata,
    };
  }

  /**
   * Converts legacy StudyForm to format expected by MappingEngine
   */
  static studyFormToMappingEngineForm(
    form: StudyForm | (LegacyStudyForm & { fields?: unknown[] })
  ): StudyForm {
    // If it's already the API-backed shape, return as-is.
    if ('study_id' in form && 'json_schema' in form) {
      return form as StudyForm;
    }

    const legacy = form as LegacyStudyForm & { fields?: unknown[] };
    return {
      id: legacy.id,
      study_id: 0,
      name: legacy.name,
      json_schema: {},
      type: legacy.type,
      created_at: '',
      updated_at: '',
      order: 0,
      fields: (legacy.fields || []) as unknown as StudyForm['fields'],
    };
  }

  /**
   * Runs smart auto-mapping using new MappingEngine and returns legacy format
   */
  static async runSmartAutoMapping(
    files: LegacySelectedFile[],
    forms: LegacyStudyForm[],
    autoMappingEnabled: boolean
  ): Promise<LegacyFileToFormMapping[]> {
    if (!autoMappingEnabled || files.length === 0 || forms.length === 0) {
      return [];
    }

    const autoMappings: LegacyFileToFormMapping[] = [];

    // Process files sequentially to avoid UI freezing
    for (const file of files) {
      try {
        const mappingEngineFile = this.selectedFileToMappingEngineFile(file);
        const mappingEngineForms = forms.map(f =>
          this.studyFormToMappingEngineForm(f)
        );

        const result = MappingEngine.smartAutoMap(
          mappingEngineFile,
          mappingEngineForms
        );

        if (result.formDetected && result.fileMapping.formId) {
          // Convert to legacy format
          const fileId =
            file.source === 'uploaded'
              ? file.file!.name
              : `csv-${file.csvFile!.id}`;

          autoMappings.push({
            fileId,
            formId: result.fileMapping.formId,
            confidence: result.formConfidence,
            matchType: result.fileMapping.matchType as
              | 'exact'
              | 'fuzzy'
              | 'manual',
          });
        }
      } catch (error) {
        console.warn(`Failed to auto-map file:`, error);
      }
    }

    return autoMappings;
  }

  /**
   * Creates FileMapping objects for the new mapping system
   * This will be used when we fully migrate to the new system
   */
  static createFileMappings(
    files: LegacySelectedFile[],
    forms: LegacyStudyForm[],
    autoMappingEnabled: boolean
  ): Record<string, FileMapping> {
    const fileMappings: Record<string, FileMapping> = {};

    files.forEach(file => {
      const fileId =
        file.source === 'uploaded'
          ? file.file!.name
          : `csv-${file.csvFile!.id}`;

      const mappingEngineFile = this.selectedFileToMappingEngineFile(file);
      const mappingEngineForms = forms.map(f =>
        this.studyFormToMappingEngineForm(f)
      );

      let fileMapping: FileMapping;

      if (autoMappingEnabled && forms.length > 0) {
        // Try smart auto-mapping
        const result = MappingEngine.smartAutoMap(
          mappingEngineFile,
          mappingEngineForms
        );
        fileMapping = result.fileMapping;
      } else {
        // Create empty mapping
        const defaultForm =
          forms.length > 0 ? this.studyFormToMappingEngineForm(forms[0]) : null;
        fileMapping = MappingEngine.createMapping(
          file.source === 'uploaded' ? file.file!.name : file.csvFile!.filename,
          defaultForm || {
            id: 0,
            study_id: 0,
            name: 'Unknown',
            json_schema: {},
            type: 1,
            created_at: '',
            updated_at: '',
            order: 0,
            fields: [],
          }
        );
      }

      fileMappings[fileId] = fileMapping;
    });

    return fileMappings;
  }
}
